import { useState } from 'react';
import { api } from '../api.js';
import { Card, SectionHeader, Button } from './ui';
import { useAiCache, generatedAgo } from '../hooks/useAiCache.js';

// Phase 4 item 4 — rejection/ghosting pattern detector. On-demand: the operator
// clicks Find patterns; the backend gathers the closed-negative applications and
// (only if there are enough) has Claude cluster them into named themes with raw
// counts + hedged insights. Below the floor it returns {insufficient} with no
// token spend, and we show how many more are needed. Nothing auto-runs.

export default function AnalyticsClustersCard() {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  // T262 — survives a navigation; the operator paid for this result once.
  const { data, generatedAt, save } = useAiCache('clusters');
  const [error, setError] = useState(null);

  const run = async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await api.post('/analytics/clusters', {});
      save(res);
      setStatus('done');
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not analyze the closed applications.');
      setStatus('error');
    }
  };

  const loading = status === 'loading';
  return (
    <section className="space-y-2">
      <SectionHeader
        title="Why applications don't convert"
        hint="On demand: clusters your closed applications (rejected / withdrawn / gone quiet) by a shared trait — country, remote, seniority, source, score. Hedged correlations, never proven causes. Needs a handful of closed apps; uses your Anthropic key."
      />
      <Card padding="md">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" onClick={run} disabled={loading}>
            {loading ? 'Analyzing…' : data && !data.insufficient ? 'Re-analyze' : 'Find patterns'}
          </Button>
          {data && !data.insufficient && generatedAt && !loading && (
            <span className="text-[10px] text-slate-500">generated {generatedAgo(generatedAt)}</span>
          )}
        </div>

        {status === 'error' && <p className="mt-3 text-sm text-red-400">{error}</p>}

        {!loading && data?.insufficient && (
          <p className="mt-3 text-sm text-slate-400">
            Not enough closed applications to find patterns yet — {data.have} of about {data.need}. This fills in as more
            applications close out (rejected, withdrawn, or gone quiet).
          </p>
        )}

        {!loading && data && !data.insufficient && (
          <div className="mt-3 space-y-3">
            {data.themes.length === 0 ? (
              <p className="text-sm text-slate-400">
                No clear pattern — your {data.analyzed} closed applications are too varied to cluster honestly.
              </p>
            ) : (
              data.themes.map((t, i) => (
                <div key={i} className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-200">{t.name}</p>
                    <span className="shrink-0 text-xs tabular-nums text-amber-400">{t.count}</span>
                  </div>
                  <p className="mt-0.5 text-xs uppercase tracking-wide text-slate-500">{t.sharedTrait}</p>
                  <p className="mt-1.5 text-sm text-slate-300">{t.insight}</p>
                  {t.examples?.length > 0 && (
                    <p className="mt-1 truncate text-xs text-slate-500">{t.examples.join(' · ')}</p>
                  )}
                </div>
              ))
            )}
            {data.note && <p className="text-xs text-slate-500">{data.note}</p>}
            <p className="text-[10px] text-slate-600">
              {data.analyzed} closed applications analyzed by {data.model} · {(data.tokensIn ?? 0) + (data.tokensOut ?? 0)} tokens.
            </p>
          </div>
        )}
      </Card>
    </section>
  );
}
